import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import MentoriaFAQ, { getMentoriaFaqs } from "@/components/mentoring/MentoriaFAQ";
import { useLanguage } from "@/i18n/LanguageContext";
import MentoringSalesSection from "@/components/MentoringSalesSection";
import MentoringVipLanding from "@/components/mentoring/MentoringVipLanding";

import { Handshake, Users, TrendingUp, ShieldCheck } from "lucide-react";

import { useLocalizedRoute } from "@/hooks/useLocalizedRoute";

const Mentoria = () => {
  useLocalizedRoute();
  const { lang } = useLanguage();
  const es = lang === "es";

  const seoTitle = es
    ? "Mentor de Retail: Asesoría Personalizada | Retail Lemon"
    : "Retail Mentor: Personalized Advisory | Retail Lemon";
  const seoDesc = es
    ? "Mentor de retail dedicado a tu proyecto. Extra mensual desde 280 €/mes; el panel se contrata aparte. Sin permanencia, resultados medibles, experiencia de trinchera."
    : "Dedicated retail mentor for your project. Monthly extra from €280/month; the panel is purchased separately. No commitment, measurable results, field-tested expertise.";
  const seoKeywords = es
    ? "consultoría retail precios, mentoría retail estratégico, asesoramiento retail, dirección retail externalizada"
    : "retail consulting pricing, strategic retail mentoring, retail advisory, outsourced retail management";

  const mentoriaFaqs = getMentoriaFaqs(lang);

  const jsonLd: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "Retail Lemon Mentoring",
      description: "Mentoría especializada para retailers",
      url: "https://retaillemon.com/mentoria",
      priceRange: "€€",
      areaServed: { "@type": "Country", name: "ES" }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: es ? "Mentoría Retail Estratégica" : "Strategic Retail Mentoring",
      provider: {
        "@type": "ProfessionalService",
        name: "Retail Lemon",
        url: "https://retaillemon.com",
      },
      description: es
        ? "Acompañamiento experto y permanente para tu negocio retail. Planes de mentoría adaptados a cada fase."
        : "Expert, ongoing support for your retail business. Mentoring plans tailored to every stage.",
      areaServed: "Worldwide",
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: es ? "Planes de Mentoría Retail" : "Retail Mentoring Plans",
        itemListElement: [
          { "@type": "Offer", name: es ? "Mentor Quincenal" : "Mentor Biweekly", price: "280", priceCurrency: "EUR" },
          { "@type": "Offer", name: es ? "Mentor Semanal" : "Mentor Weekly",  price: "480", priceCurrency: "EUR" },
          { "@type": "Offer", name: es ? "Mentor Intensivo" : "Mentor Intensive",  price: "800", priceCurrency: "EUR" },
        ],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: mentoriaFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const benefits = es
    ? [
        { icon: Handshake, title: "Acompañamiento real", desc: "No es formación online: es un mentor dedicado a tu proyecto." },
        { icon: Users, title: "Para cualquier fase", desc: "Desde la idea inicial hasta la expansión multi-local." },
        { icon: TrendingUp, title: "Resultados medibles", desc: "KPIs, auditorías y seguimiento mensual de tu progreso." },
        { icon: ShieldCheck, title: "Mentoría Flexible", desc: "Nos adaptamos a las necesidades que tengas en cualquier momento." },
      ]
    : [
        { icon: Handshake, title: "Real support", desc: "Not online training: a dedicated mentor for your project." },
        { icon: Users, title: "For any stage", desc: "From the initial idea to multi-location expansion." },
        { icon: TrendingUp, title: "Measurable results", desc: "KPIs, audits, and monthly tracking of your progress." },
        { icon: ShieldCheck, title: "Flexible Mentoring", desc: "The mentoring service adapts to you." },
      ];

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={es ? "/mentoria-retail" : "/en/mentoring"}
        pathEs="/mentoria-retail"
        pathEn="/en/mentoring"
        lang={lang}
        keywords={seoKeywords}
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        {/* New VIP Landing prepended above */}
        <MentoringVipLanding />

        {/* Benefits */}
        <section className="bg-secondary/30 border-b border-border">
          <div className="max-w-6xl mx-auto px-4 py-20 sm:py-24">
            <h2 className="text-center font-body text-3xl sm:text-4xl font-extrabold mb-12 max-w-3xl mx-auto leading-tight">
              {es
                ? "Tu copiloto estratégico para crecer con seguridad"
                : "Your strategic co-pilot to grow with confidence"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
              {benefits.map((b) => (
                <div key={b.title} className="text-center flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-foreground flex items-center justify-center shadow-md">
                    <b.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-body text-lg font-bold leading-tight">{b.title}</h3>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-[220px]">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="planes-vip" className="max-w-5xl mx-auto px-4 pt-20 sm:pt-24 pb-16 scroll-mt-20">
          <MentoringSalesSection />
        </section>

        {/* FAQ */}
        <MentoriaFAQ />
      </main>
      <Footer />
    </>
  );
};

export default Mentoria;
